import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  ShoppingBag,
  BookOpen,
  GraduationCap,
  Users,
  Heart,
  Tag,
  User,
  Menu,
  X,
  Search,
  Bell,
  ShoppingCart,
  MessageCircle,
  LogIn,
  LogOut,
  Loader2,
  Plus,
  Flame,
  Settings,
  Play,
  Rocket,
  Heart as HeartIcon,
  Brain,
  Lock,
  Radar,
  Beaker
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { globalSearch, type SearchResults } from '../../services/searchService';
import { notificationService } from '../../services/supabaseService';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: 'District', path: '/district', icon: <Home size={18} /> },
  { label: 'Clips', path: '/clips', icon: <Play size={18} /> },
  { label: 'Drops', path: '/drops', icon: <Flame size={18} /> },
  { label: 'Market', path: '/market', icon: <ShoppingBag size={18} /> },
  { label: 'Culture', path: '/culture', icon: <BookOpen size={18} /> },
  { label: 'Learn', path: '/learn', icon: <GraduationCap size={18} /> },
  { label: 'Community', path: '/community', icon: <Users size={18} /> },
  { label: 'Sole Mates', path: '/sole-mates', icon: <Heart size={18} /> },
  { label: 'Sell', path: '/sell', icon: <Tag size={18} /> },
  { label: 'Vault', path: '/vault', icon: <Lock size={18} /> },
  { label: 'Radar', path: '/drop-radar', icon: <Radar size={18} /> },
  { label: 'Lab', path: '/lab', icon: <Beaker size={18} /> },
  { label: 'Heart', path: '/heart', icon: <HeartIcon size={18} /> },
  { label: 'AI', path: '/intelligence', icon: <Brain size={18} /> },
  { label: 'Grow', path: '/grow', icon: <Rocket size={18} /> },
];

export const Navigation: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, signOut } = useAuth();
  const { itemCount } = useCart();

  const isActive = (path: string) => location.pathname === path;

  // Load unread notification count
  useEffect(() => {
    if (!user) return;
    const loadCount = async () => {
      const { count } = await notificationService.getUnreadCount(user.id);
      setUnreadCount(count);
    };
    loadCount();
    const unsub = notificationService.subscribeToNew(user.id, () => {
      setUnreadCount(prev => prev + 1);
    });
    return unsub;
  }, [user]);

  // Search with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      const results = await globalSearch(searchQuery, 5);
      setSearchResults(results);
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  // Close search on navigate
  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/district');
  };

  const hasResults = searchResults && (searchResults.profiles.length > 0 || searchResults.listings.length > 0 || searchResults.posts.length > 0);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-district-black/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-full mx-auto px-3 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/district" className="flex items-center">
              <span className="text-xl lg:text-2xl font-bold tracking-tighter">
                DISTRICT<span className="text-district-red">88</span>
              </span>
            </Link>

            {/* Desktop Nav Items */}
            <div className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'nav-link-active' : ''}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
              {/* Post Button */}
              {isLoggedIn && (
                <Link to="/post/new" className="flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-4 sm:py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-full transition-colors">
                  <Plus size={18} className="sm:hidden" />
                  <span className="hidden sm:inline text-lg leading-none">+</span>
                  <span className="hidden lg:inline">Post</span>
                </Link>
              )}

              {/* Admin Control Center */}
              {user?.isAdmin && (
                <Link to="/admin/import" className="p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center text-yellow-400 hover:text-yellow-300 transition-colors" title="Admin: Import Product">
                  <Settings size={20} />
                </Link>
              )}

              {/* Search */}
              <button onClick={() => setSearchOpen(!searchOpen)} className="p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-100 hover:text-district-white transition-colors">
                <Search size={20} />
              </button>

              {/* Notifications - hidden on small mobile */}
              <Link to="/notifications" className="hidden sm:flex p-2.5 min-w-[40px] min-h-[40px] items-center justify-center text-gray-100 hover:text-district-white transition-colors relative">
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-district-red rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Messages - hidden on small mobile */}
              <Link
                to="/messages"
                className="hidden sm:flex p-2.5 min-w-[40px] min-h-[40px] items-center justify-center text-gray-100 hover:text-district-white transition-colors relative"
              >
                <MessageCircle size={20} />
              </Link>

              {/* Cart */}
              <Link to="/cart" className="p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-100 hover:text-district-white transition-colors relative">
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-district-red rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </Link>

              {/* Auth / Profile */}
              {isLoggedIn ? (
                <div className="hidden lg:flex items-center space-x-3">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 p-1.5 text-gray-100 hover:text-district-white transition-colors"
                  >
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.username} className="w-7 h-7 rounded-full object-cover border border-gray-700" />
                    ) : (
                      <User size={20} />
                    )}
                    <span className="text-sm font-medium max-w-[100px] truncate">{user?.username}</span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="Sign out"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden lg:flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <LogIn size={16} />
                  <span>Sign In</span>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center text-district-white"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 bg-gray-900 border-b border-gray-800 shadow-2xl"
            >
              <div className="max-w-2xl mx-auto p-4">
                <div className="relative">
                  <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search users, listings, posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                  />
                  {searching && <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 animate-spin" />}
                </div>

                {/* Results */}
                {hasResults && (
                  <div className="mt-4 space-y-4 max-h-96 overflow-y-auto">
                    {/* Users */}
                    {searchResults!.profiles.length > 0 && (
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Users</h4>
                        {searchResults!.profiles.map(p => (
                          <Link key={p.id} to={`/creator/${p.id}`} onClick={() => setSearchOpen(false)}
                            className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors">
                            <div className="w-8 h-8 bg-gray-700 rounded-full overflow-hidden">
                              {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="flex items-center justify-center h-full text-xs font-bold text-gray-400">{p.username.charAt(0).toUpperCase()}</span>}
                            </div>
                            <div>
                              <div className="text-sm font-medium">{p.display_name || p.username}</div>
                              <div className="text-xs text-gray-500">@{p.username}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Listings */}
                    {searchResults!.listings.length > 0 && (
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Listings</h4>
                        {searchResults!.listings.map(l => (
                          <Link key={l.id} to={`/market/product/${l.id}`} onClick={() => setSearchOpen(false)}
                            className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded-lg transition-colors">
                            <div className="w-8 h-8 bg-gray-700 rounded-lg overflow-hidden">
                              {(l.images as string[])?.[0] && <img src={(l.images as string[])[0]} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{l.title}</div>
                              <div className="text-xs text-gray-500">${Number(l.price)} &middot; {l.condition}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}

                    {/* Posts */}
                    {searchResults!.posts.length > 0 && (
                      <div>
                        <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Posts</h4>
                        {searchResults!.posts.map(p => (
                          <div key={p.id} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                            <div className="text-sm truncate">{p.content}</div>
                            <div className="text-xs text-gray-500">by @{p.author?.username}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {searchQuery.length >= 2 && !searching && !hasResults && (
                  <div className="text-center py-6 text-gray-500 text-sm">No results found</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-30 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="absolute inset-0 bg-district-black/95"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.nav
              className="absolute top-16 left-0 right-0 bottom-0 bg-district-black border-t border-gray-800 overflow-y-auto"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="px-6 py-8 space-y-2">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider
                        ${isActive(item.path) 
                          ? 'text-district-white bg-gray-900 border-l-2 border-district-red' 
                          : 'text-gray-100 hover:text-district-white hover:bg-gray-900'
                        } transition-all duration-300`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: navItems.length * 0.05 }}
                  className="pt-4 mt-4 border-t border-gray-800"
                >
                  {isLoggedIn ? (
                    <>
                      <Link
                        to="/post/new"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-red-400 hover:text-red-300 hover:bg-gray-900 transition-all duration-300"
                      >
                        <Plus size={18} />
                        <span>Create Post</span>
                      </Link>
                      <Link
                        to="/messages"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-gray-100 hover:text-district-white hover:bg-gray-900 transition-all duration-300 sm:hidden"
                      >
                        <MessageCircle size={18} />
                        <span>Messages</span>
                      </Link>
                      {user?.isAdmin && (
                        <Link
                          to="/admin/import"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-yellow-400 hover:text-yellow-300 hover:bg-gray-900 transition-all duration-300"
                        >
                          <Settings size={18} />
                          <span>Import Product</span>
                        </Link>
                      )}
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-gray-100 hover:text-district-white hover:bg-gray-900 transition-all duration-300"
                      >
                        {user?.avatar ? (
                          <img src={user.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <User size={18} />
                        )}
                        <span>{user?.username || 'Profile'}</span>
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-gray-100 hover:text-district-white hover:bg-gray-900 transition-all duration-300"
                      >
                        <ShoppingBag size={18} />
                        <span>My Orders</span>
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-gray-100 hover:text-district-white hover:bg-gray-900 transition-all duration-300"
                      >
                        <Settings size={18} />
                        <span>Settings</span>
                      </Link>
                      <button
                        onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                        className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-red-400 hover:text-red-300 hover:bg-gray-900 transition-all duration-300 w-full text-left"
                      >
                        <LogOut size={18} />
                        <span>Sign Out</span>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-4 px-4 py-4 text-lg uppercase tracking-wider text-district-red hover:text-red-300 hover:bg-gray-900 transition-all duration-300"
                    >
                      <LogIn size={18} />
                      <span>Sign In</span>
                    </Link>
                  )}
                </motion.div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
